import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useConsultation } from '../context/ConsultationContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getImageUrl } from '../config';
import { settingsService } from '../services/settingsService';

const ConsultationContainer = styled.div`
  min-height: calc(100vh - 200px);
  padding: 2rem 0;
`;

const ConsultationContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ConsultationTitle = styled.h1`
  color: var(--primary-blue);
  font-size: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const EmptyConsultation = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  h2 {
    color: var(--gray-medium);
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--gray-medium);
    margin-bottom: 2rem;
  }
`;

const ContinueShoppingButton = styled.button`
  padding: 12px 24px;
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background: #081050;
  }
`;

const ConsultationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ConsultationItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ConsultationItem = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 100px 1fr auto auto;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 80px 1fr;
    gap: 1rem;
  }
`;

const ItemImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: var(--border-radius);
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ItemInfo = styled.div`
  h3 {
    color: var(--gray-dark);
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
  
  p {
    color: var(--gray-medium);
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  
  .category {
    background: var(--gray-light);
    color: var(--gray-dark);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.8rem;
    display: inline-block;
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    grid-column: 1 / -1;
    justify-content: center;
    margin-top: 1rem;
  }
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid var(--gray-medium);
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background: var(--primary-blue);
    color: white;
    border-color: var(--primary-blue);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  min-width: 40px;
  text-align: center;
  font-weight: 600;
  font-size: 1.1rem;
`;

const RemoveButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background: #c0392b;
  }
  
  @media (max-width: 768px) {
    grid-column: 1 / -1;
    margin-top: 0.5rem;
  }
`;

const ConsultationSummary = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  padding: 2rem;
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.h3`
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  
  &.total {
    font-weight: 600;
    font-size: 1.2rem;
    color: var(--primary-blue);
    border-top: 2px solid var(--gray-light);
    padding-top: 1rem;
    margin-top: 1.5rem;
  }
`;

const WhatsAppButton = styled.button`
  width: 100%;
  padding: 15px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  
  &:hover {
    background: #1da851;
  }
`;

const Consultation: React.FC = () => {
  const { consultation, updateQuantity, removeFromConsultation, itemCount, generateWhatsAppMessage, clearConsultation } = useConsultation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [whatsappNumber, setWhatsappNumber] = useState('+51962623633'); // valor por defecto

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromConsultation(productId);
      toast.info('Producto eliminado de la consulta');
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId: string, productName: string) => {
    removeFromConsultation(productId);
    toast.success(`${productName} eliminado de la consulta`);
  };

  // Cargar número de WhatsApp al montar el componente
  useEffect(() => {
    const loadWhatsappNumber = async () => {
      try {
        const settings = await settingsService.getSettings();
        if (settings.whatsapp_number) {
          setWhatsappNumber(settings.whatsapp_number);
        }
      } catch (error) {
        // Error handling without console logging for production
      }
    };
    loadWhatsappNumber();
  }, []);

  const handleWhatsAppConsultation = () => {
    if (!user) {
      toast.error('Debes iniciar sesión para realizar una consulta');
      navigate('/login');
      return;
    }

    const message = generateWhatsAppMessage(); // Ahora viene sin codificar
    const encodedMessage = encodeURIComponent(message); // Se codifica una sola vez aquí
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    
    // Limpiar el carrito después de enviar a WhatsApp
    clearConsultation();
    
    toast.success('Consulta enviada a WhatsApp.');
  };

  if (consultation.items.length === 0) {
    return (
      <ConsultationContainer>
        <ConsultationContent>
          <ConsultationTitle>
            Mi Carrito
          </ConsultationTitle>
          <EmptyConsultation>
            <h2>Tu Carrito está vacío</h2>
            <p>¡Agrega algunos productos para comenzar!</p>
            <ContinueShoppingButton onClick={() => navigate('/productos')}>
              Ver Productos
            </ContinueShoppingButton>
          </EmptyConsultation>
        </ConsultationContent>
      </ConsultationContainer>
    );
  }

  return (
    <ConsultationContainer>
      <ConsultationContent>
        <ConsultationTitle>
          Mi Carrito ({itemCount} productos)
        </ConsultationTitle>
        
        <ConsultationGrid>
          <ConsultationItems>
            {consultation.items.map(item => (
              <ConsultationItem key={item.product.id}>
                <ItemImage 
                  src={getImageUrl(item.product.image_url)}
                  alt={item.product.name}
                  onError={(e) => {
                    if (e && e.target) {
                      (e.target as HTMLImageElement).src = '/placeholder-image.jpg';
                    }
                  }}
                />
                
                <ItemInfo>
                  <h3>{item.product.name}</h3>
                  <p>{item.product.description}</p>
                  <span className="category">{item.product.category_name || 'Sin categoría'}</span>
                </ItemInfo>
                
                <QuantityControls>
                  <QuantityButton 
                    onClick={() => handleQuantityChange(item.product.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    ➖
                  </QuantityButton>
                  <QuantityDisplay>{item.quantity}</QuantityDisplay>
                  <QuantityButton 
                    onClick={() => handleQuantityChange(item.product.id, item.quantity + 1)}
                    disabled={false} // Remove stock restriction
                  >
                    ➕
                  </QuantityButton>
                  <RemoveButton 
                  onClick={() => handleRemoveItem(item.product.id, item.product.name)}
                  title="Eliminar producto"
                >
                  🗑️
                </RemoveButton>
                </QuantityControls>
                
                
              </ConsultationItem>
            ))}
          </ConsultationItems>
          
          <ConsultationSummary>
            <SummaryTitle>Resumen de Consulta</SummaryTitle>
            
            {consultation.items.map(item => (
              <SummaryItem key={item.product.id}>
                <span>{item.product.name} x{item.quantity}</span>
              </SummaryItem>
            ))}
            
            <SummaryItem className="total">
              <span>Total de productos:</span>
              <span>{itemCount}</span>
            </SummaryItem>
            
            <WhatsAppButton onClick={handleWhatsAppConsultation}>
              Consultar por WhatsApp
            </WhatsAppButton>
            
            <ContinueShoppingButton 
              onClick={() => navigate('/productos')}
              style={{ width: '100%', marginTop: '1rem' }}
            >
              Ver Más Productos
            </ContinueShoppingButton>
          </ConsultationSummary>
        </ConsultationGrid>
      </ConsultationContent>
    </ConsultationContainer>
  );
};

export default Consultation;